/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Icon, IIconStyleProps, IIconStyles } from '@fluentui/react/lib/Icon';
import { Stack, StackItem } from '@fluentui/react/lib/Stack';
import { getFocusStyle, getTheme, IStyle, mergeStyles } from '@fluentui/react/lib/Styling';
import { classNamesFunction, getId } from '@fluentui/react/lib/Utilities';
import * as React from 'react';
import { CalloutButton } from 'src/core/components/CalloutButton';
import { LuisSeparator } from 'src/core/components/LuisSeparator';
import { LuisText } from 'src/core/components/LuisText';
import { TruncatedText } from 'src/core/components/TruncatedText';
import { useResponsiveMode } from 'src/core/hooks/useResponsiveMode';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { noop } from 'src/core/utils/base';
import { LuisKeyCodes } from 'src/core/utils/keyCodes';

export type ExpandableCardProps = {
    id?: string;
    title?: string;
    cardName?: string;
    isExpanded?: boolean;
    minimizable?: boolean;
    isTruncatedText?: boolean;
    onCardClick?: () => void;
    isChevronHidden?: boolean;
    titleDescription?: string;
    children?: React.ReactNode;
    isSeparatorDisplayed?: boolean;
    onRenderHeader?: React.ReactNode;
    onExpansionChange?: (isExpanded: boolean) => void;
    styles?: { root?: IStyle; headerText?: IStyle; headerContainer?: IStyle; contentContainer?: IStyle };
};

const cardTheme = getScopedTheme('ExpandableCard');

const headerFocusStyles = mergeStyles(getFocusStyle(getTheme(), { inset: 1 }));
const defaultHeaderStyles: IStyle = { padding: cardTheme.headerSpacing };
const defaultRootStyles: IStyle = { boxShadow: cardTheme.boxShadow, backgroundColor: cardTheme.backgroundColor };

export const ExpandableCard = (props: ExpandableCardProps) => {
    const {
        id,
        title,
        cardName,
        children,
        onRenderHeader,
        titleDescription,
        onCardClick = noop,
        isExpanded = false,
        isTruncatedText = true,
        minimizable = true,
        isChevronHidden = false,
        onExpansionChange = noop,
        isSeparatorDisplayed = true,
        styles = { root: null, headerContainer: null, contentContainer: null, headerText: null }
    } = props;

    const stackId = React.useRef<string>(getId());
    const headerId = React.useRef<string>(getId());
    const { isSmallOrGreater } = useResponsiveMode();
    const [isContentExpanded, setIsContentExpanded] = React.useState(isExpanded);

    const expandingIconStyles = classNamesFunction<IIconStyleProps, IIconStyles>()({
        root: { fontSize: 12, transform: `rotate(${isContentExpanded ? '0' : '180deg'})`, transition: 'transform 300ms ease-in-out' }
    });

    React.useEffect(() => setIsContentExpanded(isExpanded), [isExpanded]);

    const keyUpHandler = (e: React.KeyboardEvent<HTMLElement>) => {
        if (e.key === LuisKeyCodes.Enter || e.key === LuisKeyCodes.Space) {
            handleHeaderClick();
        }
    };

    const renderHeader = () =>
        onRenderHeader ?? (
            <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 10 }}>
                {isTruncatedText ? (
                    <TruncatedText fontWeight={600} variant="mediumPlus" data-automation-id="title" maxWidth={isSmallOrGreater && 'none'}>
                        <span className={mergeStyles(styles.headerText)}>{title}</span>
                    </TruncatedText>
                ) : (
                    <LuisText fontWeight={600} variant="mediumPlus" data-automation-id="title" shouldWrapText>
                        <span className={mergeStyles(styles.headerText)}>{title}</span>
                    </LuisText>
                )}

                {titleDescription ? <CalloutButton content={titleDescription} /> : null}
            </Stack>
        );

    const handleHeaderClick = () => {
        setIsContentExpanded(!isContentExpanded);
        onExpansionChange(!isContentExpanded);
    };

    return (
        <Stack grow id={id} key={id} onClick={onCardClick} styles={{ root: mergeStyles(defaultRootStyles, styles.root) }}>
            <Stack
                horizontal
                aria-label={cardName}
                id={headerId.current}
                verticalAlign="center"
                data-automation-id="header"
                tokens={{ childrenGap: 8 }}
                className={headerFocusStyles}
                aria-controls={stackId.current}
                tabIndex={minimizable ? 0 : -1}
                role={minimizable ? 'button' : null}
                onKeyUp={minimizable ? keyUpHandler : undefined}
                onClick={minimizable ? handleHeaderClick : undefined}
                aria-expanded={minimizable ? isContentExpanded : null}
                styles={{
                    root: mergeStyles(defaultHeaderStyles, styles.headerContainer, { cursor: minimizable ? 'pointer' : 'default' })
                }}>
                <StackItem data-automation-id="headerText" styles={{ root: { overflow: 'hidden', maxWidth: '100%' } }} grow>
                    {renderHeader()}
                </StackItem>

                {!isChevronHidden ? <Icon iconName="ChevronUp" styles={expandingIconStyles} /> : null}
            </Stack>

            {isSeparatorDisplayed && <LuisSeparator />}

            {isContentExpanded ? (
                <Stack
                    grow
                    id={stackId.current}
                    data-automation-id="content"
                    aria-labelledby={headerId.current}
                    styles={{ root: mergeStyles(styles.contentContainer) }}>
                    {children}
                </Stack>
            ) : null}
        </Stack>
    );
};
